import { useEffect, useRef, useState } from "react";
import { Pencil, Languages, ArrowRight, ArrowLeft } from "lucide-react";
import { useAppContext } from "@/providers";
import { useToast } from "@/components/ui/toast";
import { Input } from "@/components/ui/input";
import { EditorialModal, ModalSection } from "@/components/ui/editorial-modal";
import { t } from "@/lib/i18n/messages";
import { updateInitiative } from "@/lib/api/initiatives";
import { translateInitiative } from "@/lib/api/utility";
import { cn } from "@/lib/utils";
import type { Initiative, UpdateInitiativeInput } from "@/types/initiative";

interface EditInitiativeModalProps {
  initiative: Initiative;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditInitiativeModal({
  initiative,
  onClose,
  onSuccess,
}: EditInitiativeModalProps) {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const { toast } = useToast();
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  const [form, setForm] = useState<UpdateInitiativeInput>({
    title: initiative.title_en || initiative.title,
    title_ar: initiative.title_ar,
    description: initiative.description_en || initiative.description,
    description_ar: initiative.description_ar,
    start_date: initiative.start_date?.split("T")[0] ?? "",
    end_date: initiative.end_date?.split("T")[0] ?? "",
    visibility: (initiative.visibility as "public" | "private") ?? "public",
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [translating, setTranslating] = useState(false);

  const handleAutoTranslate = async () => {
    const titleEn = form.title ?? "";
    if (!titleEn.trim()) return;
    setTranslating(true);
    try {
      const res = await translateInitiative({
        initiative_id: initiative.id,
        text: titleEn,
      });
      if (res.success && res.data?.translations) {
        setForm((f) => ({ ...f, title_ar: res.data!.translations }));
      } else {
        toast(isRTL ? "فشل في الترجمة" : "Translation failed", "error");
      }
    } catch {
      toast(isRTL ? "فشل في الترجمة" : "Translation failed", "error");
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setFieldErrors({});
    setSubmitting(true);
    try {
      const res = await updateInitiative(initiative.id, form);
      if (res.success) {
        toast(
          isRTL ? "تم تحديث المبادرة بنجاح" : "Initiative updated successfully",
          "success",
        );
        onSuccess();
        onClose();
      } else {
        const errors: Record<string, string> = {};
        if (res.errors) {
          Object.entries(res.errors).forEach(([k, msgs]) => {
            errors[k] = msgs[0] ?? "";
          });
        } else {
          errors["title"] = res.message ?? t(locale, "error");
        }
        setFieldErrors(errors);
      }
    } catch {
      setFieldErrors({ title: t(locale, "error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EditorialModal
      isRTL={isRTL}
      onClose={onClose}
      heroIcon={Pencil}
      eyebrow={isRTL ? "تعديل · المبادرة" : "EDIT · INITIATIVE"}
      title={t(locale, "editInitiative")}
      subtitle={
        isRTL
          ? "حدِّث العنوان، الوصف، والمواعيد."
          : "Update the title, description, and dates."
      }
      tone="primary"
      closeLabel={t(locale, "close")}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-12 rounded-pill border border-border text-foreground text-[14px] font-bold hover:bg-muted transition-colors disabled:opacity-50"
          >
            {t(locale, "cancelAction")}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={submitting}
            className={cn(
              "flex-1 h-12 rounded-pill inline-flex items-center justify-center gap-2",
              "bg-primary hover:bg-primary-dark text-white",
              "text-[14px] font-bold shadow-sm transition-all active:scale-[0.98]",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                <span>{isRTL ? "جارٍ الحفظ..." : "Saving..."}</span>
              </span>
            ) : (
              <>
                <span>{t(locale, "saveChanges")}</span>
                {isRTL ? (
                  <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                ) : (
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                )}
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <ModalSection
          num="01"
          title={isRTL ? "العنوان" : "Title"}
        >
          <div className="space-y-3">
            <Input
              ref={firstInputRef}
              dir="ltr"
              label={isRTL ? "العنوان (إنجليزي)" : "Title (English)"}
              value={form.title ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
              error={fieldErrors["title"]}
            />
            <div className="space-y-2">
              <Input
                dir="rtl"
                label={isRTL ? "العنوان (عربي)" : "Title (Arabic)"}
                value={form.title_ar ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title_ar: e.target.value }))
                }
                error={fieldErrors["title_ar"]}
              />
              <button
                type="button"
                onClick={handleAutoTranslate}
                disabled={translating || !form.title?.trim()}
                className="inline-flex items-center gap-1.5 font-sans text-[10px] tracking-[0.2em] uppercase text-primary hover:text-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Languages className="w-3 h-3" strokeWidth={2} />
                {translating
                  ? isRTL
                    ? "جارٍ الترجمة..."
                    : "Translating..."
                  : t(locale, "autoTranslate")}
              </button>
            </div>
          </div>
        </ModalSection>

        <ModalSection
          num="02"
          title={isRTL ? "الوصف" : "Description"}
        >
          <div className="space-y-3">
            <TextareaField
              label={isRTL ? "الوصف (إنجليزي)" : "Description (English)"}
              dir="ltr"
              value={form.description ?? ""}
              onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            />
            <TextareaField
              label={isRTL ? "الوصف (عربي)" : "Description (Arabic)"}
              dir="rtl"
              value={form.description_ar ?? ""}
              onChange={(v) => setForm((f) => ({ ...f, description_ar: v }))}
            />
          </div>
        </ModalSection>

        <ModalSection
          num="03"
          title={isRTL ? "المواعيد والرؤية" : "Schedule & visibility"}
        >
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="date"
              label={t(locale, "startDate")}
              value={form.start_date ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, start_date: e.target.value }))
              }
            />
            <Input
              type="date"
              label={t(locale, "endDate")}
              value={form.end_date ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, end_date: e.target.value }))
              }
            />
          </div>
          <div className="space-y-2 mt-3">
            <label className="font-sans text-[10px] tracking-widest text-foreground-secondary uppercase">
              {isRTL ? "إمكانية الرؤية" : "Visibility"}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(["public", "private"] as const).map((v) => {
                const isActive = (form.visibility ?? "public") === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, visibility: v }))
                    }
                    className={cn(
                      "h-11 rounded-2xl border text-[13px] font-bold transition-all",
                      isActive
                        ? "bg-primary/10 border-primary text-primary"
                        : "bg-white border-neutral-200 text-foreground-secondary hover:border-neutral-300",
                    )}
                  >
                    {v === "public"
                      ? isRTL
                        ? "عام"
                        : "Public"
                      : isRTL
                        ? "خاص"
                        : "Private"}
                  </button>
                );
              })}
            </div>
          </div>
        </ModalSection>
      </form>
    </EditorialModal>
  );
}

function TextareaField({
  label,
  dir,
  value,
  onChange,
}: {
  label: string;
  dir: "ltr" | "rtl";
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block font-sans text-[10px] tracking-widest text-foreground-secondary uppercase mb-2">
        {label}
      </label>
      <textarea
        dir={dir}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full px-4 py-3 rounded-2xl bg-white border outline-none transition-colors resize-none",
          "text-[15px] text-foreground placeholder:text-foreground-secondary/60",
          "border-neutral-200 hover:border-neutral-300 focus:border-neutral-500",
          "dark:bg-card dark:border-border dark:hover:border-border-hover dark:focus:border-foreground/40",
        )}
      />
    </div>
  );
}
